import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { firstValueFrom, forkJoin, lastValueFrom, map, merge, Observable, of, share, shareReplay, zip } from "rxjs";
import { Dataset } from "@models/mood/dataset";
import { ParentVectorDataset, VectorDataset } from "@models/mood/vector";
import { ApplicationCoreModule } from "../core/core.module";

import { Category } from '@models/mood/covariates/category';
import { AuthService } from "../core/auth/auth.service";
import { Provider } from "@models/mood/general/provider";



@Injectable({
  providedIn: ApplicationCoreModule
})
export class DatasetListingService {
  cachedDatasets$!: Observable<Dataset[]>;
  cachedVectorDatasets$!: Observable<ParentVectorDataset[]>;

  cachedDatasetCategories$!: Observable<Category[]>;
  cachedProviders$!: Observable<Provider[]>;


  constructor(
    private _authService: AuthService,
    private _httpClient: HttpClient
  ) { }

  getDatasetCategories(): Promise<Category[]> {
    if (!this.cachedDatasetCategories$) {
      this.cachedDatasetCategories$ = this._httpClient.get<Category[]>(`api/category`).pipe(
        shareReplay(1)
      );
    }
    return firstValueFrom(this.cachedDatasetCategories$);
  }

  async updateDatasetCategoryByid(category_id: string, entry: Category) {
    let categories = await this.getDatasetCategories();

    const categoryIndex = categories.findIndex(category => category.id == category_id);

    if (categoryIndex === -1) {
      categories.push(entry);
    } else {
      categories[categoryIndex] = entry;
    }

    this.cachedDatasetCategories$ = of(categories);
  }

  getDatasetListing(): Promise<Dataset[]> {
    if (!this.cachedDatasets$) {
      this.cachedDatasets$ = this._httpClient.get<Dataset[]>(`api/dataset`).pipe(
        shareReplay(1)
      );
    }
    return firstValueFrom(this.cachedDatasets$);
  }

  async findDatasetById(id: string) {
    let datasets = await this.getDatasetListing();
    return datasets.find(dataset => dataset.id === id);
  }

  async updateDatasetById(id: string, entry: Dataset) {
    let datasets = await this.getDatasetListing();

    const datasetIndex = datasets.findIndex(set => set.id == id);

    if (datasetIndex != -1) {
      datasets[datasetIndex] = entry;
    } else {
      datasets.push(entry);
    }
    this.cachedDatasets$ = of(datasets);
  }

  getVectorDatasetListing(): Promise<ParentVectorDataset[]> {
    if (!this.cachedVectorDatasets$) {
      this.cachedVectorDatasets$ = this._httpClient.get<ParentVectorDataset[]>(`api/vector-dataset`).pipe(
        map(array => {
          return array.map(set => {
            return { ...set, type: 'VECTOR' } as ParentVectorDataset
          })
        }),
        shareReplay(1)
      );
    }
    return firstValueFrom(this.cachedVectorDatasets$);
  }

  async findVectorDatasetById(id: string) {
    let vectorDatasets = await this.getVectorDatasetListing();
    return vectorDatasets.find(dataset => dataset.id === id);
  }

  async findVectorDataById(id: string) {
    let vectorData = await this._httpClient.get<VectorDataset>(`api/vector-data/${id}`).pipe(
      shareReplay(1)
    );
    return firstValueFrom(vectorData);
  }

  async getProviders(): Promise<Provider[]> {
    if (!this.cachedProviders$) {
      this.cachedProviders$ = this._httpClient.get<Provider[]>(`api/provider`).pipe(
        shareReplay(1)
      );
    }
    return firstValueFrom(this.cachedProviders$);
  }
}
